import { useCallback } from 'react';
import { useObservableCacheItem } from '../../cache/observable-cache-hooks';
import { useObservableCache } from '../../cache/observable-cache-provider';
import { logger } from '../../util/logger';
import {
  DirectConnectTask,
  DirectConnectTaskType,
} from '../model/direct-connect.model';

const cacheKey = 'dc-tasks';

const useDirectConnectGetTaskList = (
  serialNumber: string,
  taskType?: DirectConnectTaskType
): DirectConnectTask[] => {
  const result = useObservableCacheItem<DirectConnectTask[]>(cacheKey);

  return (
    result?.filter((r) => {
      let shouldReturn = r.serialNumber === serialNumber;

      if (taskType) {
        shouldReturn = shouldReturn && r.taskType === taskType;
      }

      return shouldReturn;
    }) ?? []
  );
};

const useDirectConnectGetTask = (
  serialNumber: string,
  taskType: DirectConnectTaskType
): DirectConnectTask | undefined => {
  const tasks = useDirectConnectGetTaskList(serialNumber) ?? [];
  return tasks.find(
    (r) => r.serialNumber === serialNumber && r.taskType === taskType
  );
};

const useDirectConnectGetTaskLazy = (): ((
  serialNumber: string,
  taskType: DirectConnectTaskType
) => Promise<DirectConnectTask | undefined>) => {
  const cache = useObservableCache();

  const callback = useCallback(
    async (serialNumber: string, taskType: DirectConnectTaskType) => {
      const data = await cache.getItemFromList<DirectConnectTask>(
        cacheKey,
        (entry) =>
          entry.serialNumber === serialNumber && entry.taskType === taskType
      );
      return data ?? undefined;
    },
    [cache]
  );

  return callback;
};

const useDirectConnectRemoveTask = (): ((
  serialNumber: string,
  taskType: DirectConnectTaskType
) => Promise<void>) => {
  const cache = useObservableCache();

  const callback = useCallback(
    async (serialNumber: string, taskType: DirectConnectTaskType) => {
      logger.info('removing direct connect task', {
        serialNumber,
        taskType,
      });

      await cache.filterList<DirectConnectTask>(cacheKey, (entry) => {
        if (
          entry.serialNumber === serialNumber &&
          entry.taskType === taskType
        ) {
          return false;
        }

        return true;
      });
    },
    [cache]
  );

  return callback;
};

const useDirectConnectRemoveAllTask = (): ((
  serialNumber: string
) => Promise<void>) => {
  const cache = useObservableCache();

  const callback = useCallback(
    async (serialNumber: string) => {
      logger.info('removing all direct connect task', {
        serialNumber,
      });

      await cache.filterList<DirectConnectTask>(cacheKey, (entry) => {
        return entry.serialNumber !== serialNumber;
      });
    },
    [cache]
  );

  return callback;
};

const useDirectConnectAddTask = (): ((
  request: DirectConnectTask
) => Promise<void>) => {
  const cache = useObservableCache();

  const callback = useCallback(
    async (request: DirectConnectTask) => {
      logger.info('adding direct connect task to cache', {
        serialNumber: request.serialNumber,
        taskType: request.taskType,
      });

      await cache.addItemToList<DirectConnectTask>(
        cacheKey,
        request,
        (itemToAdd, entry) => {
          return (
            itemToAdd.serialNumber === entry.serialNumber &&
            itemToAdd.taskType === entry.taskType
          );
        }
      );
    },
    [cache]
  );

  return callback;
};

export {
  useDirectConnectAddTask,
  useDirectConnectGetTask,
  useDirectConnectGetTaskLazy,
  useDirectConnectGetTaskList,
  useDirectConnectRemoveTask,
  useDirectConnectRemoveAllTask,
};
